import { useContext, createSignal, Show, createEffect, on } from 'solid-js';
import { gql } from 'graphql-request';
import { Grid } from '../../grid-system/grid/grid';
import { StyledButtonWrapper, StyledContentHubContainer, StyledLinksContainer, StyledLinksInner, StyledRightPanel } from './content-hub.styles';
import { createGroupedBlogAndNewsCard } from '../blogs-news/blogs-news-component';
import { BlogNewsData, BlogNewsListCardData } from '../blog-news/blog-news';
import { EventData, EventListCardData } from '../event/event-types';
import { PostCardPlaceholder } from './post-card-placeholder';
import { ContentHubProps, CustomPostLink } from './content-hub-types';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { Button } from '../../ui-components/button/button';
import { ErrorCatcher } from '../../tools/error-catcher';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { createGroupedEventCard } from '../events/helpers/events-card-helper';
import { isOnDemandEvent } from '../events/helpers/is-on-demand-event';
import { mapBlogMetaToListCardData } from '../blogs-news/map-blog-meta-to-list-card-data';
import { mapEventMetaToListCardData } from '../events/helpers/map-event-meta-to-list-card-data';

const GET_POSTS_QUERY_WITH_TAGS = gql`
    blogPostsWithTags: resources(
        type: "blog-and-news"
        tags: $blogTags
        limit: $limit
        order: DESC
    ) {
        key
        meta {
            postTitle
            categories
            description
            featuredImage
            extra
        }
    }
`;

const GET_POSTS_QUERY = gql`
    blogPosts: resources(
        type: "blog-and-news"
        limit: $limit
        order: DESC
    ) {
        key
        meta {
            postTitle
            categories
            description
            featuredImage
            extra
        }
    }
`;

const GET_EVENTS_QUERY_WITH_TAGS = gql`
    eventsWithTags: resources(
        type: "event",
        tags: $eventTags,
        limit: $limit,
        order: DESC,
        fromDate: $fromDate
    ) {
        key
        meta {
            postTitle
            categories
            description
            featuredImage
            extra
        }
    }
`;

const GET_EVENTS_QUERY = gql`
    events: resources(
        type: "event",
        limit: $limit,
        order: DESC,
        fromDate: $fromDate
    ) {
        key
        meta {
            postTitle
            categories
            description
            featuredImage
            extra
        }
    }
`;

export const GET_CUSTOM_POSTS_QUERY = gql`
    query GetCustomPosts(
        $keys: [String],
    ) {
        posts: resources(
            keys: $keys
        ) {
            content
        }
    }

`;

type QueryVariables = {
    limit: number;
    blogTags?: string[];
    eventTags?: string[];
    fromDate?: string
};

export const ContentHub = (props: ContentHubProps) => {
    const { createCachedResource, localize, blogAndNewsEntityName } = useContext(AppContext);

    const [err, setErr] = createSignal(false);
    const [renderedCards, setRenderedCards] = createSignal<any[]>([<PostCardPlaceholder />, <PostCardPlaceholder />, <PostCardPlaceholder />]);

    // Debugging signals:
    const [card1Type, setCard1Type] = createSignal<string | undefined>();
    const [card1Tag, setCard1Tag] = createSignal<string | undefined>();
    const [card2Type, setCard2Type] = createSignal<string | undefined>();
    const [card2Tag, setCard2Tag] = createSignal<string | undefined>();
    const [card3Type, setCard3Type] = createSignal<string | undefined>();
    const [card3Tag, setCard3Tag] = createSignal<string | undefined>();
    const [card1PostTags, setCard1Tags] = createSignal<string[]>([]);
    const [card2PostTags, setCard2PostTags] = createSignal<string[]>([]);
    const [card3PostTags, setCard3PostTags] = createSignal<string[]>([]);
    const [filteredContentForCard1, setFilteredContentForCard1] = createSignal<any[]>([]);
    const [filteredContentForCard2, setFilteredContentForCard2] = createSignal<any[]>([]);
    const [filteredContentForCard3, setFilteredContentForCard3] = createSignal<any[]>([]);
    const [card1IncludesOnDemand, setCard1IncludesOnDemand] = createSignal<boolean>(false);
    const [card2IncludesOnDemand, setCard2IncludesOnDemand] = createSignal<boolean>(false);
    const [card3IncludesOnDemand, setCard3IncludesOnDemand] = createSignal<boolean>(false);
    const [noPostsViableForIndexes, setNoPostsViableForIndexes] = createSignal<number[]>([]);
    const [indexIsSetToCustomPost, setIndexIsSetToCustomPost] = createSignal<number[]>([]);
    const [card1PreviousAttemptedPosts, setCard1PreviousAttemptedPosts] = createSignal<any[]>([]);
    const [card2PreviousAttemptedPosts, setCard2PreviousAttemptedPosts] = createSignal<any[]>([]);
    const [card3PreviousAttemptedPosts, setCard3PreviousAttemptedPosts] = createSignal<any[]>([]);
    const [card1CustomPostLink, setCard1CustomPostLink] = createSignal<CustomPostLink | undefined>();
    const [card2CustomPostLink, setCard2CustomPostLink] = createSignal<CustomPostLink | undefined>();
    const [card3CustomPostLink, setCard3CustomPostLink] = createSignal<CustomPostLink | undefined>();
    const [cardsCustomPostSkipped, setCardsCustomPostSkipped] = createSignal<number[]>([]);
    // End debugging signals

    if (props.mixedContent && props.cards.length === 0) {
        return null;
    }
    
    let blogTags: string[] = [];
    let eventTags: string[] = [];

    const shouldFetchByDate = props.mixedContent
        ? props.cards.filter((card) => card?.posttype?.value === 'event').length > 0
        : props.contentType?.posttype?.value === 'event';

    const eventTagsPlaceholder = 'EVENT_TAGS';
    const blogTagsPlaceholder = 'BLOG_TAGS';

    // Create a graphql query to load everything at once, both events and blog-and-news
    let query = `query GetEntries(
        $limit: Int,
        ${eventTagsPlaceholder}
        ${blogTagsPlaceholder}
        ${shouldFetchByDate ? '$fromDate: String' : ''}
    ) { `;

    const prepareTagsAndQuery = (
        postType: string,
        tagsPlaceholder: string,
        getPostsQuery: string,
        getPostsQueryWithTags: string,
        queryPlaceholderReplacement: string
    ) => {
        const posts = props.cards
            .filter(card => !card.customPostLink?.url)
            .filter(card => card.posttype?.value === postType);
    
        // Graphql doesn't like empty variables, so we need to remove the placeholder
        if (posts.length === 0) {
            query = query.replace(tagsPlaceholder, '');
            return;
        }   
    
        /**
         * Fetch all posts, even if all cards specify a tag, so that we have a bunch of backups to choose from.
         */
        query += getPostsQuery;
    
        const tags = Array.from(new Set(posts
            .filter(card => card.tag?.slug)
            .map(card => card.tag?.slug) as string[]));

        if (tags.length > 0) {
            query = query.replace(tagsPlaceholder, queryPlaceholderReplacement);            
            query += getPostsQueryWithTags;

            // Store the tags for later use in the variables
            postType === 'blog-and-news' 
                ? blogTags = tags 
                : eventTags = tags;

            return;
        }

        // Graphql doesn't like empty variables, so we need to remove the placeholder
        query = query.replace(tagsPlaceholder, '');
    };

    /**
     * If we have mixed content we need to massage the graphql query string to include the
     * correct queries based on the cards we have.
     */
    if (props.mixedContent) {
        prepareTagsAndQuery(
            'blog-and-news',
            blogTagsPlaceholder,
            GET_POSTS_QUERY,
            GET_POSTS_QUERY_WITH_TAGS,
            '$blogTags: [String],'
        );

        prepareTagsAndQuery(
            'event',
            eventTagsPlaceholder,
            GET_EVENTS_QUERY,
            GET_EVENTS_QUERY_WITH_TAGS,
            '$eventTags: [String],'
        );
    } else {
        // Fetch all posts, and also posts with specific tags (that way we ensure we get all posts with the tag, but also backups)
        if (props.contentType?.posttype?.value === 'blog-and-news') {
            blogTags = props.contentType.tag?.slug ? [props.contentType.tag.slug] : [];
            query += GET_POSTS_QUERY;
            query = query.replace(blogTagsPlaceholder, '$blogTags: [String],');
            query = query.replace(eventTagsPlaceholder, ''); // Graphql doesn't like empty variables
            query += GET_POSTS_QUERY_WITH_TAGS;
        } else {
            eventTags = props.contentType.tag?.slug ? [props.contentType.tag.slug] : [];
            query += GET_EVENTS_QUERY;
            query = query.replace(eventTagsPlaceholder, '$eventTags: [String],');
            query = query.replace(blogTagsPlaceholder, ''); // Graphql doesn't like empty variables
            query += GET_EVENTS_QUERY_WITH_TAGS;
        }
    }

    query += ' } ';

    const variables: QueryVariables = { 
        limit: 50, // Even though we only show 3 cards, we load an arbitrarily higher amount to maximize change that we have enough after we've filtered out the ones we don't want
        fromDate: new Date().toDateString()
    };

    variables.eventTags = eventTags;
    variables.blogTags = blogTags;

    const [entries] = createCachedResource(query, variables, true);


    /**
     * Fetch data for custom post links using the collected keys.
     * First ensure we have keys - request with empty keys will fetch everything in database
     */
    const customPostLinks = props.cards
        .map(card => card.customPostLink?.url)
        .filter(url => url);

    let customPostLinkEntries: any;

    if (customPostLinks.length) {
        [ customPostLinkEntries ] = createCachedResource(
            GET_CUSTOM_POSTS_QUERY,
            { keys: customPostLinks },
            true
        );
    }

    const audienceFilter = (post: BlogNewsData | EventData) => {
        if (!props.audience || props.audience === 'all') {
            return true;
        }
        if (props.audience === 'hcp') {
            return post?.isHealthcareProfessional;
        } else {
            return !post?.isHealthcareProfessional;
        }
    };

    const eventsFilter = (event: EventListCardData, cardIndex: number, type: string) => {
        if (type === 'blog-and-news') {
            return true;
        }

        if (event?.isFullyBooked) {
            return false;
        }

        if (!isActiveEvent(event?.eventExpireDate, event?.eventDate)) {
            return false;
        }

        if (isOnDemandEvent(event)) {
            if (props.contentType?.posttype?.value === 'event' && !props.globalIncludeAndPreferOnDemandEvents) {
                // We're showing only "events" type (not "mixed" or "blog-and-news") - hence respect the global on-demaind setting
                return false;
            }

            if (props.mixedContent && !props.cards[cardIndex]?.includeAndPreferOnDemandEvents) {
                // We are dealing with the "mix" content type, and that the current card is of type "event".
                // Respect the specific card's includeAndPreferOnDemandEvents attribute
                // If the card does not include on-demand events, we filter them out
                return false;
            }
        }

        return true;
    };

    const renderCustomLinkCard = (customPostLink: CustomPostLink, type: string, largeCard: boolean) => {
        // Find the corresponding entry from customPostLinkEntries based on the URL
        const cardData = customPostLinkEntries()?.posts.find((entry: any) => entry.content?.permalink === customPostLink.url);
        if (!cardData) {
            return largeCard
                ? <Heading tag="h3" variant="small">{localize('no-content-found', 'No content found')}</Heading>
                : null;
        }
        if (type === 'blog-and-news') {
            return createGroupedBlogAndNewsCard({ post: cardData.content, largeCard });
        } else if (type === 'event') {
            return createGroupedEventCard({ event: cardData.content, largeCard });
        }

        return null;
    };

    function getPostSlug(card: any) {
        return card?.slug || card?.permalink || card?.key;
    }

    // Recursive function to find the next viable post that hasn't been rendered yet
    function getNextUniquePost(cardIndex: number, type: string | undefined, tag: string, filteredData: any[], alreadyRenderedSlugs: string[]): any {
        /**
         * Find the data item to render based on the tag and the count of the same type of card we've already rendered.
         */
        const filtered = filteredData
            .filter((content: any) => {
                const tags = content?.tags || [];
                return tag === 'NO_TAG' ? true : tags.includes(tag);
            });

        if (props.debugModeActive) {
            if (cardIndex === 0) {
                setFilteredContentForCard1(filtered);
            } else if (cardIndex === 1) {
                setFilteredContentForCard2(filtered);
            } else if (cardIndex === 2) {
                setFilteredContentForCard3(filtered);
            }
        }
        
        // Find the first item that hasn't been rendered yet
        let offset = 0;
        while (offset < filtered.length) {
            const candidate = filtered[offset];
            if (!candidate) break;
            const slug = getPostSlug(candidate);

            if (!alreadyRenderedSlugs.includes(slug)) {
                return candidate;
            }

            offset++;
        }
        return null;
    }

    createEffect(
        on(
            () => entries(),
            () => {                
                const readyCardsData: any[] = [];
                if (entries.error) {
                    console.log('error', entries.error);
                    setErr(true);
                    return;
                }

                if (entries() === undefined) {
                    // We haven't received any entries yet (if there are no entries it will instead be an empty array)
                    return;
                }

                const numberOfCards = 3;
                const alreadyRenderedSlugs: string[] = [];

                // Helper function to render a card at a given index
                function renderCardAtIndex(i: number) {
                    const currentCard = props.cards[ i ];
                    const largeCard = i === 0;

                    // CUSTOM POST LINK HANDLING
                    const customPostLink = currentCard?.customPostLink;

                    if (customPostLink?.url) {
                        if (props.debugModeActive) {
                            setIndexIsSetToCustomPost((prev: number[]) => [ ...prev, i ]);
                            if (i === 0) {
                                setCard1CustomPostLink(customPostLink);
                            } else if (i === 1) {
                                setCard2CustomPostLink(customPostLink);
                            } else if (i === 2) {
                                setCard3CustomPostLink(customPostLink);
                            }

                            if (alreadyRenderedSlugs.includes(customPostLink?.url)) {
                                console.log(`Custom post link ${customPostLink?.url} already rendered, skipping...`);
                                setCardsCustomPostSkipped((prev: number[]) => [ ...prev, i ]);
                            }
                        }

                        if (!alreadyRenderedSlugs.includes(customPostLink?.url)) {
                            alreadyRenderedSlugs.push(customPostLink?.url);                  
                            readyCardsData.push(renderCustomLinkCard(customPostLink, currentCard.posttype.value, largeCard));
                        }

                        return;
                    }

                    // TYPE AND TAG HANDLING
                    let tag: string;
                    let type: string;

                    if (props.mixedContent) {
                        tag = currentCard?.tag?.slug || 'NO_TAG';
                        type = currentCard.posttype.value;
                    } else {
                        tag = props.contentType?.tag?.slug || 'NO_TAG';
                        type = props.contentType.posttype.value;
                    }

                    let post = parseCardsData(i, alreadyRenderedSlugs, type, tag, largeCard);

                    if (!post) {
                        // Don't want empty cards, so relax requirements and try again.
                        console.log(`No post found for index ${i} with type ${type} and tag ${tag}, trying without tag`);

                        if (props.debugModeActive) {
                            setNoPostsViableForIndexes((prev: number[]) => [ ...prev, i ]);
                            if (i === 0) {
                                setCard1PreviousAttemptedPosts(filteredContentForCard1());
                            } else if (i === 1) {
                                setCard2PreviousAttemptedPosts(filteredContentForCard2());
                            } else if (i === 2) {
                                setCard3PreviousAttemptedPosts(filteredContentForCard3());
                            }
                        }

                        post = parseCardsData(i, alreadyRenderedSlugs, type, 'NO_TAG', largeCard);
                    }

                    if (props.debugModeActive) {
                        if (i === 0) {
                            setCard1Type(type);
                            setCard1Tag(tag);
                            setCard1Tags(post?.tags || []);
                        } else if (i === 1) {
                            setCard2Type(type);
                            setCard2Tag(tag);
                            setCard2PostTags(post?.tags || []);
                        } else if (i === 2) {
                            setCard3Type(type);
                            setCard3Tag(tag);
                            setCard3PostTags(post?.tags || []);
                        }
                    }

                    if (!post) {
                        // We've previously tried finding a post with and without tag, but still no post found - continuing here will 
                        // result in the next card's post taking this card's place (i.e. the effect is that the
                        // next card will be rendered in this card's place), but that will be rare if posts are set dynamically
                        // (if they use custom posts, then empty slots will be more common)
                        console.log(`No post found for index ${i} with type ${type} and tag ${tag} - continuing to next card`);
                        return;
                    }

                    const slug = getPostSlug(post);
                    if (!alreadyRenderedSlugs.includes(slug)) {
                        alreadyRenderedSlugs.push(slug);
                    }

                    if (type === 'blog-and-news') {
                        const blogCard = createGroupedBlogAndNewsCard({ post: post, largeCard });
                        readyCardsData.push(blogCard);
                    } else if (type === 'event') {
                        const eventCard = createGroupedEventCard({ event: post, largeCard });
                        readyCardsData.push(eventCard);
                    }
                }

                // Main loop
                for (let i = 0; i < numberOfCards; i++) {
                    renderCardAtIndex(i);
                }

                // If not enough cards, try to fill up the rest by rendering more cards
                if (readyCardsData.length < numberOfCards) {
                    for (let i = readyCardsData.length; i < numberOfCards; i++) {
                        renderCardAtIndex(i);
                    }
                }

                setRenderedCards(readyCardsData);
            }
        )
    );

    const isActiveEvent = (expiryDate?: string, eventDate?: string) => {
        /**
         * eventExpiryDate takes precedence over eventDate - one is for on-demand events, 
         * the other for live events, but nothing prevents editors from setting both
         */
        const date = expiryDate || eventDate;
        
        if (!date) return true;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const eventDateObj = new Date(date);
        eventDateObj.setHours(0, 0, 0, 0);

        return eventDateObj >= today;
    };

    const sortOnDemandEventsFirst = (filteredData: (EventListCardData | BlogNewsListCardData)[]) => {
        const onDemandEvents: EventListCardData[] = [];
        const nonOnDemandEvents: EventListCardData[] = [];

        filteredData.forEach((data: any) => {
            if (isOnDemandEvent(data)) {
                onDemandEvents.push(data);
            } else {
                nonOnDemandEvents.push(data);
            }
        });

        // Sort on-demand events by reverse date, so the most recent one is at the top
        onDemandEvents.sort((a, b) => {
            const dateA = a?.date ? new Date(a.date).getTime() : 0;
            const dateB = b?.date ? new Date(b.date).getTime() : 0;
            return dateB - dateA; // Sort descending
        });

        return [ ...onDemandEvents, ...nonOnDemandEvents ];
    };

    const parseCardsData = (cardIndex: number, alreadyRenderedSlugs: string[], type: string, tag: string = 'NO_TAG', largeCard: boolean = false) => {
        const data = type === 'blog-and-news'
            ? [
                ...entries()?.blogPosts || [],
                ...entries()?.blogPostsWithTags || []
            ]
            : [
                ...entries()?.events || [],
                ...entries()?.eventsWithTags || []
            ];

        const filteredData: (EventListCardData | BlogNewsListCardData | null)[] = data
            .map((data: any) => {
                if (type === 'blog-and-news') {
                    return mapBlogMetaToListCardData(data, blogAndNewsEntityName);
                }
                if (type === 'event') {
                    return mapEventMetaToListCardData(data);
                }
                return null;
            })
            .sort((a, b) => {
                const dateA = a?.date ? new Date(a.date).getTime() : 0;
                const dateB = b?.date ? new Date(b.date).getTime() : 0;
                if (type === 'event') {
                    return dateA - dateB;
                } else {
                    return dateB - dateA;
                }
            })
            .filter((content: any) => Boolean(content.url))
            .filter((content: any) => audienceFilter(content))
            .filter((content: any) => eventsFilter(content, cardIndex, type));

        
        const eventsContentTypeAndOnDemandIncludedAndPreferred = (props.contentType?.posttype?.value === 'event')
            && props.globalIncludeAndPreferOnDemandEvents;

        const currentCardIsEventAndOnDemandIncludedAndPreferred = (type === 'event') 
            && props.cards[cardIndex]?.includeAndPreferOnDemandEvents;

        const includesOnDemandEvents = eventsContentTypeAndOnDemandIncludedAndPreferred || currentCardIsEventAndOnDemandIncludedAndPreferred;

        if (includesOnDemandEvents) {
            // filteredData will include on-demand events if we get here. Now put them at the top of the list, as they
            // should be shown first 
            const eventsDataWithOnDemandAtTheTop = sortOnDemandEventsFirst(filteredData as EventListCardData[]);

            filteredData.length = 0; // Clear the array
            filteredData.push(...eventsDataWithOnDemandAtTheTop);

            if (props.debugModeActive) {
                if (cardIndex === 0) {
                    setCard1IncludesOnDemand(true);
                } else if (cardIndex === 1) {
                    setCard2IncludesOnDemand(true);
                } else if (cardIndex === 2) {
                    setCard3IncludesOnDemand(true);
                }
            }
        }

        if (!filteredData) {
            return largeCard
                ? <Heading tag="h3" variant="small">{localize('no-content-found', 'No content found')}</Heading>
                : null;
        }
        
        const post = getNextUniquePost(cardIndex, type, tag, filteredData, alreadyRenderedSlugs);
        return post;
    };

    function withLinks() {
        return Boolean(props.link1?.url || props.link2?.url);
    };

    const Debugging = (props: { cardIndex: number; isDebugging?: boolean }) => {
        const mapFunc = (item: any) => (
            <li>{`${item.permalink} ..... ${item.date}`}</li>
        );

        const cardType = () => props.cardIndex === 0 ? card1Type() : props.cardIndex === 1 ? card2Type() : card3Type();
        const cardTag = () => props.cardIndex === 0 ? card1Tag() : props.cardIndex === 1 ? card2Tag() : card3Tag();
        const cardIncludesOnDemand = () => props.cardIndex === 0 ? card1IncludesOnDemand() : props.cardIndex === 1 ? card2IncludesOnDemand() : card3IncludesOnDemand();
        const cardPostTags = () => props.cardIndex === 0 ? card1PostTags() : props.cardIndex === 1 ? card2PostTags() : card3PostTags();
        const cardCustomPostLink = () => props.cardIndex === 0 ? card1CustomPostLink() : props.cardIndex === 1 ? card2CustomPostLink() : card3CustomPostLink();
        const noPostsViable = () => noPostsViableForIndexes().includes(props.cardIndex);

        const cardFilteredContent = () => {
            if (props.cardIndex === 0) {
                return filteredContentForCard1().map(mapFunc);
            } else if (props.cardIndex === 1) {
                return filteredContentForCard2().map(mapFunc);
            } else {
                return filteredContentForCard3().map(mapFunc);
            }
        };

        return (
            <Show when={props.isDebugging}>
                <h3>{`Card ${props.cardIndex + 1}`}</h3>
                <br />

                <Show
                    when={!indexIsSetToCustomPost().includes(props.cardIndex)}
                    fallback={
                        <div style={{ 'margin-left': '20px' }}>
                            <p>Card is set to custom post link {cardCustomPostLink()?.url}</p>
                            <br />
                            <Show when={cardsCustomPostSkipped().includes(props.cardIndex)}>
                                <em>Custom post link has already been rendered - next card has been hoisted up to take its place</em>
                            </Show>
                            <br />
                            <br />
                        </div>
                    }
                >
                    <div>
                        <h4>{`Type: ${cardType()}`}</h4>
                        <h4>{`Tag: ${cardTag()}`}</h4>
                        <Show when={cardType() === 'event' && cardIncludesOnDemand()}>
                            <h4>On-demand event</h4>
                        </Show>
                        <br />
                        <Show when={noPostsViable()}>
                            <h5>No viable data available</h5>
                            <em>Will attempt to show content without the requested tag instead (or in case no data there either, as final fallback hoist the next card up one space)</em>
                            <br />
                            <br />
                            <h6>Initial viable posts to choose from:</h6>
                            <ul style={{ 'margin-left': '20px' }}>
                                {props.cardIndex === 0 ? card1PreviousAttemptedPosts().map(mapFunc) :
                                    props.cardIndex === 1 ? card2PreviousAttemptedPosts().map(mapFunc) :
                                        card3PreviousAttemptedPosts().map(mapFunc)}
                            </ul>
                            <br />
                        </Show>
                        <br />
                        <Show
                            when={noPostsViable()}
                            fallback={<h5>Data to choose from:</h5>}
                        >
                            <h5>Data to choose from (without tag requirement):</h5>
                        </Show>

                        <ul style={{ 'margin-left': '20px' }}>
                            {cardFilteredContent()}
                        </ul>
                        <Show when={!cardFilteredContent()}>
                            <div style={{ 'margin-left': '20px', 'margin-top': '10px' }}>
                                <p>No content found - the next card has been hoisted up one space to avoid empty cards</p>
                                <br />
                            </div>
                        </Show>
                        <br />
                        <h3>Post tags: {JSON.stringify(cardPostTags())}</h3>
                        <br />
                    </div>
                </Show>
            </Show>
        );
    };

    return (
        <ErrorCatcher componentName='Content hub'>
            <StyledContentHubContainer class='content-hub' withLinks={withLinks()}>
                <Show when={props.eyebrowHeading}>
                    <Heading tag="h3" variant="small">{props.eyebrowHeading}</Heading>
                </Show>
                <Show when={props.heading}>
                    <Heading tag="h2" variant="xlarge">{props.heading}</Heading>
                </Show>
                <Show
                    when={!err()}
                    fallback={
                        <Text fontSize='normal'>{localize('error-has-occurred', 'An error has occurred, please try again later')}</Text>
                    }
                >
                    <Grid
                        templateShorthand={[7, 5]}
                        responsive={{
                            largeDesktop: [6, 6],
                            smallDesktop: [6, 6],
                            tablet: [12]
                        }}
                    >
                        <div>
                            <Debugging cardIndex={0} isDebugging={props.debugModeActive} />
                            <Show when={renderedCards()[ 0 ]}>
                                {renderedCards()[ 0 ]}
                            </Show>
                        </div>
                        <Grid
                            templateShorthand={[5]} 
                            responsive={{
                                largeDesktop: [6],
                                smallDesktop: [6],
                                tablet: [12]
                            }}
                        >
                            <StyledRightPanel>
                                <div>
                                    <Debugging cardIndex={1} isDebugging={props.debugModeActive}/>
                                    <Show when={renderedCards()[ 1 ]}>
                                        {renderedCards()[ 1 ]}
                                    </Show>
                                </div>
                                <div>
                                    <Debugging cardIndex={2} isDebugging={props.debugModeActive}/>
                                    <Show when={renderedCards()[ 2 ]}>
                                        {renderedCards()[ 2 ]}
                                    </Show>
                                </div>
                                <StyledLinksContainer>
                                    <StyledLinksInner>
                                        <Show when={props.link1?.url}>
                                            <Button variant="tertiary" label={props.link1?.title} arrow={true} isLink={true} url={props.link1?.url || '#'}/>
                                        </Show>
                                        <Show when={props.link2?.url}>
                                            <StyledButtonWrapper>
                                                <Button variant="tertiary" label={props.link2?.title} arrow={true} isLink={true} url={props.link2?.url || '#'}/>
                                            </StyledButtonWrapper>
                                        </Show>
                                    </StyledLinksInner>
                                </StyledLinksContainer>
                            </StyledRightPanel>
                        </Grid>
                    </Grid>
                </Show>
            </StyledContentHubContainer>
        </ErrorCatcher>
    );
};


ContentHub.parseProps = (atts: any) => {
    const type = atts.contentType?.posttype?.value;
    const mixedContent = type === 'mix' ? true : false;
    const cards = mixedContent ? [ atts.card1, atts.card2, atts.card3 ] : [];
    
    return {
        eyebrowHeading: atts.eyebrowHeading,
        heading: atts.heading,
        mixedContent,
        contentType: atts.contentType,
        cards: cards.filter(card => card?.posttype?.value),
        link1: atts.link1,
        link2: atts.link2,
        audience: atts.audience,
        globalIncludeAndPreferOnDemandEvents: atts.globalIncludeAndPreferOnDemandEvents || false,
        debugModeActive: atts.debugModeActive || false,
    };
};
